import { Edge, MarkerType, Node } from "reactflow";
import { Ref, RefSource, Table } from "./types";
import React from "react";
import { COLUMN_PREFIX, SEE_MORE_PREFIX } from "./constants";

export const REF_EDGE_PREFIX = "ref:";

// ERD overlay edge palette — desaturated to avoid competing with the
// lineage edges' own palette. Saturated purple is reserved for the PK/FK
// chips and the toolbar toggle so users read them as the "ERD signal".
// Declared truth (test / contract / semantic) shares one neutral color;
// inferred edges use a muted indigo + dashed stroke to read as "guess".
const REF_DECLARED_COLOR = "#6B7280"; // tailwind gray-500
const REF_INFERRED_COLOR = "#8B5CF6"; // tailwind violet-500, dashed

// SVG marker IDs declared in `RefEdgeMarkers` and referenced via `url(...)`
// in `createRefEdge`.
export const REF_MARKER_CROWSFOOT_DECLARED = "ref-marker-crowsfoot-declared";
export const REF_MARKER_CROWSFOOT_INFERRED = "ref-marker-crowsfoot-inferred";
export const REF_MARKER_ONE_DECLARED = "ref-marker-one-declared";
export const REF_MARKER_ONE_INFERRED = "ref-marker-one-inferred";

/**
 * Resolve an SVG marker ID to a full `url(...)` reference safe to use in
 * `markerEnd`/`markerStart` attributes inside a VS Code webview.
 *
 * VS Code webviews set a `<base>` tag whose href differs from the actual
 * document URI. Plain fragment references like `url(#myMarker)` resolve
 * against the base href, which makes the browser look up the marker on
 * a different document and silently fail. Including the current doc href
 * up to (but not including) any existing fragment forces an absolute
 * reference that always points at this document's `<defs>`.
 *
 * Falls back to the bare fragment when `window` is undefined (SSR /
 * tests) so unit tests don't crash.
 */
export const markerRefUrl = (id: string): string => {
  if (typeof window === "undefined") return `url(#${id})`;
  const docHref = window.location.href.replace(/#.*$/, "");
  return `url(${docHref}#${id})`;
};

// config constants
export const MAX_EXPAND_TABLE = 5;

// dimensions
export const F_OFFSET_X = 100;
export const F_OFFSET_Y = 100;
export const T_NODE_W = 300;
export const T_NODE_H = 80;
export const C_OFFSET_X = 12;
export const C_OFFSET_Y = T_NODE_H;
const C_NODE_H = 30;
const C_PADDING_Y = 4;
export const T_LEVEL_SEPARATION = 280;
export const LEVEL_SEPARATION_VERTICAL = 80;
export const T_NODE_Y_SEPARATION = 80;
export const T_NODE_Y_SEPARATION_VERTICAL = 250;

export const DEFAULT_MIN_ZOOM = 0.05;
// node styles
const DEFAULT_COLOR = "#7A899E";
const HIGHLIGHT_COLOR = "#E38E00";

export const VIEWS_TYPE_COLOR = {
  Original: "#FDD835",
  Alias: "#40C8AE",
  Transformation: "#FF754C",
  Unchanged: "#BC3FBC",
  "Not sure": "#247efe",
  "Non select": "#BC3FBC",
};

export type ViewsTypes = keyof typeof VIEWS_TYPE_COLOR;

export const defaultEdgeStyle: React.CSSProperties = {
  stroke: DEFAULT_COLOR,
  strokeWidth: 1,
};
export const highlightEdgeStyle: React.CSSProperties = {
  stroke: HIGHLIGHT_COLOR,
  strokeWidth: 2,
};
export const indirectHighlightEdgeStyle: React.CSSProperties = {
  stroke: HIGHLIGHT_COLOR,
  strokeWidth: 1,
  strokeDasharray: 10,
};
export const defaultMarker = {
  type: "arrow" as MarkerType,
  strokeWidth: 1,
  width: 24,
  height: 24,
  color: DEFAULT_COLOR,
};
export const highlightMarker = {
  type: "arrow" as MarkerType,
  strokeWidth: 1,
  width: 16,
  height: 16,
  color: HIGHLIGHT_COLOR,
};

export const isColumn = (x: { id: string }) => x.id.startsWith(COLUMN_PREFIX);
export const isSeeMore = (x: { id: string }) => x.id.startsWith(SEE_MORE_PREFIX);
export const isNotColumn = (x: { id: string }) => !x.id.startsWith(COLUMN_PREFIX);

export const createTableEdge = (
  n1Level: number,
  n2Level: number,
  n1: string,
  n2: string,
  right: boolean,
  isVertical = false,
  from = ""
): Edge => {
  const [src, dst] = right ? [n1, n2] : [n2, n1];
  const [sourceHandle, targetHandle] = right
    ? getSourceTargetHandles(n1Level, n2Level, isVertical)
    : getSourceTargetHandles(n2Level, n1Level, isVertical);
  console.log(from); // unused
  return {
    id: `${src}-${dst}`,
    source: src,
    target: dst,
    sourceHandle,
    targetHandle,
    style: defaultEdgeStyle,
    markerEnd: defaultMarker,
    type: n1 === n2 ? "selfConnecting" : n1Level === n2Level ? "smoothstep" : "default",
    // label: from,
  };
};

/**
 * ERD overlay: build a React Flow edge for a declared / inferred FK `Ref`.
 * Returns `undefined` when either endpoint is not present in the current
 * node set — ref edges must not dangle.
 *
 * Routing strategy (column-aware):
 *   - When both endpoint columns are mounted (user expanded both tables),
 *     anchor the edge to the column-level nodes. Each column has its own
 *     y-position so multiple FKs out of one table fan apart naturally
 *     instead of piling onto a single right-handle anchor point.
 *   - Otherwise fall back to table-level anchors with `right → left`
 *     handles. Use bezier curves so refs visually peel away from the
 *     orthogonal lineage flow even when they share endpoints.
 *
 * Visual conventions (per peer-tooling survey, REFERENCES.md):
 *   - Crow's-foot marker at the FK ("many") end, single-line marker at
 *     the PK ("one") end.
 *   - Neutral grey stroke for declared; muted indigo dashed for inferred.
 *   - No always-on edge label — the join columns surface via the PK/FK
 *     chips when the table is expanded, and via tooltip on hover (the
 *     join string is preserved on `data.label`).
 */
export const createRefEdge = (
  ref: Ref,
  nodeIds: Set<string>,
  corroboratingSources?: RefSource[]
): Edge | undefined => {
  if (!nodeIds.has(ref.from.table) || !nodeIds.has(ref.to.table)) {
    return undefined;
  }

  // Column-level anchors when both first columns exist and are mounted.
  // Composite keys anchor on the first column — the join string in
  // `data.label` reflects the full set. Guard the empty-`columns` case so we
  // never build a `column-<table>/undefined` id; fall back to table-level
  // endpoints when either side has no column.
  const firstFromCol = ref.from.columns[0] ?? null;
  const firstToCol = ref.to.columns[0] ?? null;
  const fromColumnId = firstFromCol ? getColumnId(ref.from.table, firstFromCol) : null;
  const toColumnId = firstToCol ? getColumnId(ref.to.table, firstToCol) : null;
  const columnEndpoints =
    fromColumnId !== null &&
    toColumnId !== null &&
    nodeIds.has(fromColumnId) &&
    nodeIds.has(toColumnId)
      ? { source: fromColumnId, target: toColumnId }
      : null;
  const useColumnEndpoints = columnEndpoints !== null;

  const isInferred = ref.source === "inferred";
  const color = isInferred ? REF_INFERRED_COLOR : REF_DECLARED_COLOR;
  // Pick markers based on cardinality. Crow's-foot ("many") goes on the
  // side carrying the FK (= edge source = `markerStart`); bar ("one") on
  // the side with the unique key (= edge target = `markerEnd`). For
  // one-to-one, both ends are unique → bar at both. For one-to-many or
  // many-to-many, the marker placement flips accordingly.
  const cf = isInferred ? REF_MARKER_CROWSFOOT_INFERRED : REF_MARKER_CROWSFOOT_DECLARED;
  const bar = isInferred ? REF_MARKER_ONE_INFERRED : REF_MARKER_ONE_DECLARED;
  const fromIsMany = ref.cardinality === "many-to-one" || ref.cardinality === "many-to-many";
  const toIsMany = ref.cardinality === "one-to-many" || ref.cardinality === "many-to-many";
  const markerStartId = fromIsMany ? cf : bar;
  const markerEndId = toIsMany ? cf : bar;
  const fromCol = ref.from.columns.join(", ");
  const toCol = ref.to.columns.join(", ");

  return {
    id: `${REF_EDGE_PREFIX}${ref.id}`,
    source: columnEndpoints ? columnEndpoints.source : ref.from.table,
    target: columnEndpoints ? columnEndpoints.target : ref.to.table,
    // `refEdge` is a custom edge type (see `RefEdge.tsx`) that does
    // floating attachment — anchor points are the closest borders of
    // each node's bounding box rather than fixed handles. Result: edges
    // skirt around node bodies instead of cutting through them.
    type: "refEdge",
    style: {
      stroke: color,
      strokeWidth: 1.5,
      strokeDasharray: isInferred ? "6 4" : undefined,
    },
    // ReactFlow wraps `edge.markerEnd` / `edge.markerStart` in `url('#…')`
    // before passing them to the custom edge component, which means a
    // pre-formed `url(...)` string ends up double-wrapped and unparseable.
    // Stash the raw IDs in `data` and let `RefEdge` build the marker
    // attribute directly on the SVG path it renders.
    zIndex: 900,
    data: {
      refSource: ref.source,
      refId: ref.id,
      label: ref.label ?? `${fromCol} → ${toCol}`,
      isColumnAnchored: useColumnEndpoints,
      markerEndId,
      markerStartId,
      corroboratingSources: corroboratingSources ?? [ref.source],
    },
  };
};

export const isRefEdge = (edge: { id: string }) => edge.id.startsWith(REF_EDGE_PREFIX);

/**
 * Source priority for choosing the "winner" when multiple sources declare
 * the same FK relationship. Test is most explicit, inferred is a heuristic.
 * Test → contract → semantic → inferred.
 */
const SOURCE_PRIORITY: Record<RefSource, number> = {
  test: 0,
  contract: 1,
  semantic: 2,
  inferred: 3,
};

const REF_SOURCES_ALL: ReadonlyArray<RefSource> = ["test", "contract", "semantic", "inferred"];

/**
 * Dot colors for the 4-source corroboration indicator rendered at the
 * midpoint of every ref edge. Each source has a distinct hue so users can
 * read at a glance which sources agree on a given relationship.
 */
export const SOURCE_DOT_COLORS: Record<RefSource, string> = {
  test: "#3B82F6", // blue-500
  contract: "#10B981", // emerald-500
  semantic: "#A855F7", // purple-500
  inferred: "#F59E0B", // amber-500
};

/** Stable key for grouping refs that describe the same relationship. */
const canonicalEdgeKey = (ref: Ref): string => {
  const fromCols = [...ref.from.columns].sort().join(",");
  const toCols = [...ref.to.columns].sort().join(",");
  return `${ref.from.table}|${fromCols}->${ref.to.table}|${toCols}`;
};

/**
 * Dedup and filter refs for rendering.
 *
 * Groups refs by `canonicalEdgeKey` so each unique relationship shows up
 * as one edge regardless of how many sources declared it. For each
 * group:
 *
 *   - `corroboratingSources` collects every source that contributed
 *     (independent of which sources are toggled on). Drives the dot
 *     indicator at the edge midpoint.
 *
 *   - The visible ref is picked by source priority (test > contract >
 *     semantic > inferred) restricted to *enabled* sources. If no
 *     enabled source contributes the group, the edge is hidden.
 *
 * Inferred refs below the confidence threshold are filtered before
 * grouping so they neither contribute corroboration nor become the
 * winner.
 */
export const dedupeAndFilterRefs = (
  refs: Ref[],
  enabledSources: Record<RefSource, boolean>,
  inferenceConfidenceThreshold: number
): { ref: Ref; corroboratingSources: RefSource[] }[] => {
  const groups = new Map<string, Ref[]>();
  for (const ref of refs) {
    if (ref.source === "inferred" && (ref.confidence ?? 0) < inferenceConfidenceThreshold) {
      continue;
    }
    const key = canonicalEdgeKey(ref);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(ref);
  }

  const result: { ref: Ref; corroboratingSources: RefSource[] }[] = [];
  for (const group of groups.values()) {
    const corroboratingSources = Array.from(new Set(group.map(r => r.source)));
    const enabledGroup = group.filter(r => enabledSources[r.source]);
    if (enabledGroup.length === 0) continue;
    enabledGroup.sort((a, b) => SOURCE_PRIORITY[a.source] - SOURCE_PRIORITY[b.source]);
    result.push({ ref: enabledGroup[0], corroboratingSources });
  }
  return result;
};

export { REF_SOURCES_ALL };

export const createTableNode = (_table: Table, level: number, parent: string): Node => {
  return {
    id: _table.table,
    data: { ..._table, level, parent },
    position: { x: 100, y: 100 },
    type: _table.nodeType === "dashboard" ? _table.nodeType : "table",
    width: T_NODE_W,
    height: T_NODE_H,
  };
};

export const createOpNode = (
  id: string,
  level: number,
  parent: string,
  data: { name?: string; type: string }
): Node => {
  return {
    id,
    data: { ...data, level, parent, id },
    position: { x: 100, y: 100 },
    type: "operator",
    width: T_NODE_W,
    height: T_NODE_H,
  };
};

export const createColumnNode = (
  t: string,
  c: string,
  viewsType: ViewsTypes | undefined,
  viewsCode: Record<string, [string, string][]>,
  nodeType: string,
  datatype: string = ""
): Node => {
  return {
    id: getColumnId(t, c),
    data: { column: c, table: t, viewsType, viewsCode, nodeType, datatype },
    parentNode: t,
    extent: "parent",
    draggable: false,
    type: "column",
    position: { x: 100, y: 100 },
    height: C_NODE_H,
  };
};

export type EdgeVisibility = Record<string, boolean>;

export const createColumnEdge = (
  source: string,
  target: string,
  srcLevel: number,
  dstLevel: number,
  type: string,
  edgeVisibility: EdgeVisibility
): Edge => {
  const edgeId = getColumnEdgeId(source, target);
  const [sourceHandle, targetHandle] = getSourceTargetHandles(srcLevel, dstLevel, false);
  return {
    id: edgeId,
    data: { type },
    source,
    target,
    sourceHandle,
    targetHandle,
    style: type === "direct" ? highlightEdgeStyle : indirectHighlightEdgeStyle,
    zIndex: 1000,
    markerEnd: highlightMarker,
    type: srcLevel === dstLevel ? "smoothstep" : "default",
    hidden: !edgeVisibility[type],
  };
};

export const getColumnEdgeId = (source: string, target: string) =>
  COLUMN_PREFIX + `${source}-${target}`;

export const applyNodeStyling = (n: Node, highlight: boolean) => {
  n.style = { opacity: highlight ? 1 : 0.5 };
};
export const applyEdgeStyling = (e: Edge, highlight: boolean) => {
  // ERD overlay edges keep their own palette/markers regardless of which
  // table is selected. Without this guard, selection logic overwrites the
  // ref-edge stroke and crow's-foot markers with the lineage highlight.
  if (isRefEdge(e)) {
    return;
  }
  e.style = highlight
    ? e.data?.type === "indirect"
      ? indirectHighlightEdgeStyle
      : highlightEdgeStyle
    : defaultEdgeStyle;
  e.markerEnd = highlight ? highlightMarker : defaultMarker;
};

export const getSourceTargetHandles = (l0: number, l1: number, isVertical: boolean) => {
  if (isVertical) {
    if (l0 < l1) return ["bottom", "top"];
    if (l0 > l1) return ["top", "bottom"];
    if (l0 < 0) return ["top", "top"];
    return ["bottom", "bottom"];
  }
  return ["right", "left"];
};

export const getHelperDataForCLL = (nodes: Node[], edges: Edge[]) => {
  const levelMap: Record<string, number> = {};
  nodes.forEach(n => {
    if (isNotColumn(n)) levelMap[n.id] = n.data.level;
  });
  const tableNodes: Record<string, boolean> = {};
  nodes.filter(_n => _n.type === "table").forEach(_n => (tableNodes[_n.id] = true));
  const seeMoreIdTableReverseMap: Record<string, string> = {};
  for (const e of edges) {
    if (isColumn(e)) continue;
    const sourceTableExist = tableNodes[e.source];
    const targetTableExist = tableNodes[e.target];
    if (sourceTableExist && targetTableExist) {
      continue;
    }
    if (sourceTableExist) {
      const _n = nodes.find(_n => _n.id === e.target)!;
      _n.data.tables.forEach((_t: { table: string }) => {
        seeMoreIdTableReverseMap[_t.table] = e.target;
      });
      continue;
    }
    if (targetTableExist) {
      const _n = nodes.find(_n => _n.id === e.source)!;
      _n.data.tables.forEach((_t: { table: string }) => {
        seeMoreIdTableReverseMap[_t.table] = e.source;
      });
    }
  }

  return { levelMap, tableNodes, seeMoreIdTableReverseMap };
};

export const getColumnId = (t: string, c: string) => COLUMN_PREFIX + `${t}/${c}`;
export const getSeeMoreId = (t: string, right: boolean) =>
  SEE_MORE_PREFIX + t + "-" + (right ? "1" : "0");

export const contains = (arr: [string, string][], x: [string, string]) => {
  for (const item of arr) {
    if (item[0] === x[0] && item[1] === x[1]) return true;
  }
  return false;
};

export const safeConcat = <T>(obj: Record<string, T[]>, key: string, values: T[]) => {
  obj[key] = obj[key] || [];
  obj[key].push(...values);
};

export const getColY = (columnNum: number, tableNum = 1) =>
  columnNum * (C_NODE_H + C_PADDING_Y) + tableNum * C_PADDING_Y;
export const withinInclusive = (a: number, b: number) => (v: number) => a <= v && v <= b;
export const withinExclusive = (a: number, b: number) => (v: number) => a < v && v < b;

export const deleteIfExists = <T extends Node | Edge>(arr: T[], id: string) => {
  const index = arr.findIndex(x => x.id === id);
  if (index === -1) return;
  arr.splice(index, 1);
};

export const calculateExpand = (minVal: number, maxVal: number, defaultVal: number) => {
  if (minVal === -1) return maxVal;
  if (defaultVal >= maxVal) return maxVal;
  if (defaultVal >= minVal) return defaultVal;
  return minVal;
};

export const toggleModelEdges = (edges: Edge[], isVisible: boolean, highlight = true) => {
  edges.forEach(e => {
    // Ref edges have their own visibility model (showRefs + per-source
    // toggles), unrelated to the model/column visibility split that the
    // lineage component flips when entering column-level lineage mode.
    if (isRefEdge(e)) return;
    if (!isColumn(e)) {
      e.hidden = !isVisible;
      if (highlight) applyEdgeStyling(e, isVisible);
    }
  });
};

export const toggleColumnEdges = (edges: Edge[], isVisible: boolean, highlight = true) => {
  edges.forEach(e => {
    if (isRefEdge(e)) return;
    if (isColumn(e)) {
      e.hidden = !isVisible;
      if (highlight) applyEdgeStyling(e, isVisible);
    }
  });
};

export function findSourcesSinks(edges: [string, string][]) {
  const _inDegree: Record<string, number> = {};
  const _outDegree: Record<string, number> = {};
  edges.forEach(([u, v]) => {
    _inDegree[u] = _inDegree[u] || 0;
    _inDegree[v] = (_inDegree[v] || 0) + 1;
    _outDegree[u] = (_outDegree[u] || 0) + 1;
    _outDegree[v] = _outDegree[v] || 0;
  });
  const sources = Object.keys(_inDegree).filter(k => _inDegree[k] === 0);
  const sinks = Object.keys(_outDegree).filter(k => _outDegree[k] === 0);
  return { sources, sinks };
}

// Navigation utilities
export interface NavigationInfo {
  url?: string;
  id?: string;
  table: string;
}

/**
 * Helper function to get current table ID from URL (web environment only)
 * Returns undefined in extension environments or if parsing fails
 */
export const getCurrentTableId = (): string | undefined => {
  if (typeof window === "undefined") return undefined;

  try {
    const currentUrl = window.location.href;
    const urlParts = currentUrl.split("/");

    for (let i = 0; i < urlParts.length; i++) {
      if (urlParts[i] === "datasets" || urlParts[i] === "dbt_models") {
        if (i + 1 < urlParts.length) {
          return urlParts[i + 1].split("?")[0];
        }
      }
    }
  } catch (e) {
    console.warn("Could not parse current table ID from URL:", e);
  }
  return undefined;
};

/**
 * Navigation handler for table details components
 * Handles both direct URLs and ID-based navigation
 * Gracefully degrades in extension environments
 */
export const handleTableNavigation = ({ url, id, table }: NavigationInfo): void => {
  if (typeof window === "undefined") {
    console.warn("Navigation not available in this environment");
    return;
  }

  if (url) {
    window.location.href = url;
    return;
  }

  if (id) {
    try {
      const currentUrl = window.location.href;
      const urlParts = currentUrl.split("/");

      // Find the base path (datasets or dbt_models)
      for (let i = 0; i < urlParts.length; i++) {
        if (urlParts[i] === "datasets" || urlParts[i] === "dbt_models") {
          // Replace the ID part with the new id
          if (i + 1 < urlParts.length) {
            const idPart = urlParts[i + 1].split("?");
            const encodedId = urlParts[i] === "dbt_models" ? btoa(table) : id;
            urlParts[i + 1] = encodedId + (idPart.length > 1 ? "?" + idPart[1] : "");
            window.location.href = urlParts.join("/");
            return;
          }

          // If no ID is found, add it
          const encodedId = urlParts[i] === "dbt_models" ? btoa(table) : id;
          urlParts.splice(i + 1, 0, encodedId);
          window.location.href = urlParts.join("/");
          return;
        }
      }

      // If no datasets or dbt_models path is found, default to datasets
      window.location.href = `${window.location.origin}/datasets/${id}?tab=1`;
    } catch (e) {
      console.error("Failed to navigate:", e);
    }
  }
};
